'use client';

import React, { useEffect, Suspense } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { GoogleAnalytics } from '@next/third-parties/google';

/**
 * File: components/analytics/google-analytics.tsx
 *
 * Component for adding Google Analytics tracking to the application.
 * Uses the recommended @next/third-parties library for optimal performance.
 * Follows Next.js 15 best practices for handling path and search parameters.
 */

/**
 * Constants for Google Analytics configuration
 */
const GA_MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID as string;

/**
 * Tracks page views for client-side navigation
 *
 * @param path - Current path to track
 */
const pageview = (path: string) => {
  if (!GA_MEASUREMENT_ID || typeof window === 'undefined' || !window.gtag)
    return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path,
  });
};

/**
 * Internal component that handles the analytics tracking
 * This is wrapped in a Suspense boundary by the parent component
 */
function AnalyticsTracker(): React.ReactElement {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Track page views on client-side navigation
  useEffect(() => {
    if (pathname) {
      // In Next.js 15, searchParams is already an immutable URLSearchParams object
      // Create a URL object to properly build the full path
      const url = new URL(pathname, window.location.origin);

      // Append search parameters if they exist
      if (searchParams.size > 0) {
        // Convert the URLSearchParams to a string and append to the URL
        searchParams.forEach((value, key) => {
          url.searchParams.append(key, value);
        });
      }

      // Track the full URL path
      pageview(url.pathname + url.search);
    }
  }, [pathname, searchParams]); // Re-run effect when pathname or searchParams change

  return <></>; // No UI to render, just tracking
}

/**
 * GoogleAnalyticsProvider component
 * Adds Google Analytics tracking to the entire application
 * and handles client-side navigation tracking with proper suspense boundaries
 *
 * @returns {React.ReactElement} The Google Analytics component
 */
export default function GoogleAnalyticsProvider(): React.ReactElement {
  // Only render in production environment
  if (process.env.NODE_ENV !== 'production') {
    return <></>;
  }

  return (
    <>
      <GoogleAnalytics gaId={GA_MEASUREMENT_ID} />
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
    </>
  );
}

